/**
 * Stale-tab protection for document bodies (#288).
 *
 * The server counts changes to each body in content_rev. This module remembers
 * the revision each open document was loaded at and sends it as
 * base_content_rev with that document's autosaves. If the body changed since
 * (another tab or device, an undo, an MCP write), the server answers 409, and
 * STALE_CONTENT_EVENT tells AppLayout to reload the page's data instead of
 * letting this tab overwrite the newer text.
 *
 * Seeded by detail reads (GET /tasks/:id ...) and advanced by every write
 * response for that item, so a tab's own consecutive saves - and its own
 * status or title changes - never refuse each other. Documents loaded any other
 * way have no token and keep last-write-wins, as before.
 */
import { isContentSave } from './actionTracking';

export const STALE_CONTENT_EVENT = 'dossier:stale-content';

const DOC_ROUTE = /^\/(tasks|notes|meeting-instances|scratchpads|meeting-series)\/([^/?#]+)$/;

const revs = new Map<string, number>();

function docId(path: string): string | null {
  const m = DOC_ROUTE.exec(path);
  return m ? m[2] : null;
}

/** Record the revision carried by a read or write response for a document route. */
export function noteContentRev(path: string, data: unknown): void {
  const id = docId(path);
  if (!id || !data || typeof data !== 'object' || Array.isArray(data)) return;
  const rev = (data as { content_rev?: unknown }).content_rev;
  if (typeof rev === 'number' && Number.isInteger(rev) && rev >= 0) revs.set(id, rev);
}

/** The body to send: an autosave of a known document gains base_content_rev. */
export function withContentRev(method: string, path: string, body: unknown): unknown {
  if (!isContentSave(method, body)) return body;
  const id = docId(path);
  const rev = id ? revs.get(id) : undefined;
  return rev === undefined ? body : { ...(body as object), base_content_rev: rev };
}

/** Test hook. Tokens are deliberately never cleared otherwise: an editor that flushes a pending save as it unmounts must still be checked. */
export function resetContentRevsForTest(): void {
  revs.clear();
}

export class StaleContentError extends Error {
  constructor() {
    super('Updated elsewhere');
    this.name = 'StaleContentError';
  }
}
